import React, { useEffect, useState } from "react";
import { fetchAiCredit } from "../api";

// Refresh cadence for the balance while the app is open.
const POLL_MS = 5 * 60 * 1000;
// Warn when usage reaches this percentage of the allowance.
const WARN_PERCENT = 90;

/**
 * Site-wide AI-credit notice. Renders nothing unless the meter is active
 * (soft/hard metering) AND the user's usage is high or exhausted — so it
 * is invisible during the observe/shadow rollout stages.
 */
export default function AiCreditBanner() {
  const [credit, setCredit] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      fetchAiCredit()
        .then((data) => { if (!cancelled) setCredit(data); })
        .catch(() => {}); // the banner is best-effort; never break the app
    load();
    const timer = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  if (dismissed || !credit || !credit.active || credit.percent_used == null) return null;

  const exhausted = credit.percent_used >= 100;
  const low = credit.percent_used >= WARN_PERCENT;
  if (!exhausted && !low) return null;

  const resetDate = credit.resets_at
    ? new Date(credit.resets_at).toLocaleDateString(undefined, { month: "long", day: "numeric" })
    : null;
  const blocked = exhausted && credit.enforced;

  const message = blocked
    ? `You've used this month's AI allowance. AI features resume ${resetDate ? `on ${resetDate}` : "next month"}.`
    : exhausted
      ? `You've used this month's AI allowance${resetDate ? ` — it resets on ${resetDate}` : ""}.`
      : `You're close to this month's AI usage allowance${resetDate ? ` (resets ${resetDate})` : ""}.`;

  return (
    <div
      role="status"
      className="shrink-0 flex items-center justify-between gap-3 px-4 py-2 text-sm"
      style={blocked
        ? { background: "rgba(190, 60, 60, 0.18)", color: "#f3b8b8", borderBottom: "1px solid rgba(190,60,60,0.35)" }
        : { background: "var(--gold-bg)", color: "var(--gold-bright)", borderBottom: "1px solid var(--panel-line)" }}
    >
      <span>{message}</span>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="shrink-0 px-2 opacity-70 hover:opacity-100 transition-opacity"
      >
        ✕
      </button>
    </div>
  );
}
